// Minimal fetch wrapper with CSRF support.
// Same-origin; the Django backend sets a session cookie on login and a
// CSRF cookie via /api/auth/csrf. We mirror the CSRF token in the
// X-CSRFToken header for unsafe methods.

import { getClientId } from './clientId';

export class ApiError extends Error {
  public readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
  }
}

let csrfTokenPromise: Promise<string> | null = null;

export async function getCsrfToken(): Promise<string> {
  if (!csrfTokenPromise) {
    csrfTokenPromise = fetch('/api/auth/csrf/', { credentials: 'same-origin' })
      .then(async (r) => {
        if (!r.ok) throw new ApiError(r.status, `CSRF fetch failed: ${r.status}`);
        return r.json();
      })
      .then((d: { csrfToken: string }) => d.csrfToken)
      .catch((e) => {
        csrfTokenPromise = null;
        throw e;
      });
  }
  return csrfTokenPromise;
}

/** Clear the cached CSRF token — call after logout so the next request refetches it. */
export function resetCsrfToken(): void {
  csrfTokenPromise = null;
}

const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const method = (options.method || 'GET').toUpperCase();
  const headers = new Headers(options.headers || {});
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');

  if (UNSAFE_METHODS.has(method)) {
    const token = await getCsrfToken();
    headers.set('X-CSRFToken', token);
  }

  if (!headers.has('X-Client-Id')) {
    headers.set('X-Client-Id', getClientId());
  }

  // Only auto-set Content-Type for JSON bodies; preserve FormData/URLSearchParams behavior.
  if (
    options.body &&
    !(options.body instanceof FormData) &&
    !(options.body instanceof URLSearchParams) &&
    !headers.has('Content-Type')
  ) {
    headers.set('Content-Type', 'application/json');
  }

  const resp = await fetch(path, {
    ...options,
    headers,
    credentials: 'same-origin',
  });

  if (resp.status === 204) {
    return undefined as T;
  }

  const ct = resp.headers.get('content-type') || '';
  const data: unknown = ct.includes('json') ? await resp.json() : await resp.text();

  if (!resp.ok) {
    const message =
      typeof data === 'string'
        ? data || resp.statusText
        : (data as { error?: string; detail?: string })?.error ??
          (data as { error?: string; detail?: string })?.detail ??
          resp.statusText;
    throw new ApiError(resp.status, message);
  }

  return data as T;
}

// ── Server-side LLM settings ──────────────────────────────────────────────

import type { ServerSettings } from '../types';

export async function fetchSettings(): Promise<ServerSettings | null> {
  const resp = await fetch('/api/auth/settings/', { credentials: 'same-origin' });
  if (!resp.ok) return null;
  const data = await resp.json();
  return data as ServerSettings | null;
}

export async function saveSettings(body: Record<string, unknown>): Promise<ServerSettings> {
  return apiFetch<ServerSettings>('/api/auth/settings/', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

// ── Profile & Password ──────────────────────────────────────────────────

export interface ProfileData {
  avatar: string;
  username: string;
  avatar_config?: Record<string, unknown>;
  is_public?: boolean;
}

export async function fetchProfile(): Promise<ProfileData> {
  return apiFetch<ProfileData>('/api/auth/profile/');
}

export async function updateProfile(body: {
  avatar: string;
  avatar_config?: Record<string, unknown>;
  is_public?: boolean;
}): Promise<ProfileData> {
  return apiFetch<ProfileData>('/api/auth/profile/', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function changePassword(body: {
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  return apiFetch<void>('/api/auth/change-password/', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ── Avatar Collection ────────────────────────────────────────────────────

export interface SavedAvatarData {
  id: number;
  name: string;
  config: Record<string, unknown>;
  is_active: boolean;
  like_count: number;
  user_liked: boolean;
  owner: string;
  created_at: string;
  updated_at: string;
}

export interface UserProfileData {
  username: string;
  is_public: boolean;
  is_self: boolean;
  avatar?: string;
  avatar_config?: Record<string, unknown>;
  avatar_count?: number;
  active_avatar_id?: number | null;
}

export async function fetchMyAvatars(): Promise<SavedAvatarData[]> {
  return apiFetch<SavedAvatarData[]>('/api/avatars/');
}

export async function createAvatar(body: {
  name: string;
  config: Record<string, unknown>;
}): Promise<SavedAvatarData> {
  return apiFetch<SavedAvatarData>('/api/avatars/', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateAvatar(
  id: number,
  body: { name?: string; config?: Record<string, unknown> },
): Promise<SavedAvatarData> {
  return apiFetch<SavedAvatarData>(`/api/avatars/${id}/`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function deleteAvatar(id: number): Promise<void> {
  return apiFetch<void>(`/api/avatars/${id}/`, { method: 'DELETE' });
}

export async function activateAvatar(id: number): Promise<void> {
  return apiFetch<void>(`/api/avatars/${id}/activate/`, { method: 'POST' });
}

export async function toggleAvatarLike(
  id: number,
): Promise<{ liked: boolean; like_count: number }> {
  return apiFetch<{ liked: boolean; like_count: number }>(
    `/api/avatars/${id}/like/`,
    { method: 'POST' },
  );
}

export async function fetchUserAvatars(username: string): Promise<SavedAvatarData[]> {
  return apiFetch<SavedAvatarData[]>(`/api/users/${username}/avatars/`);
}

export async function fetchUserProfile(username: string): Promise<UserProfileData> {
  return apiFetch<UserProfileData>(`/api/users/${username}/profile/`);
}
